/* eslint-disable no-nested-ternary */
import React from 'react';

import { Dots, Reply, ThumbsUp, ThumbsUp5Fill } from '@crehana/icons';

import { Avatar, Popper, MenuItem, colors, unescapeChars } from '@crehana/ui';

import { useOpenState } from '@crehana/react-hooks';
import { useTranslation } from '@crehana/i18n';

import { date } from '@crehana/utils';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';
import { ChatNode } from 'Jsx/CrehanaLive/graphql/types/ChatNode';
import { ChatActions } from 'Jsx/CrehanaLive/hooks/useChatActions';

type Handlers = Partial<
  Pick<
    ChatActions,
    | 'setMessageAsQuestion'
    | 'setPinnedMessage'
    | 'hideMessage'
    | 'blockUser'
    | 'likeLiveQuestion'
    | 'likedMessages'
  >
>;

interface MessageCardProps extends ChatNode, Handlers {
  areActionsEnabled?: boolean;
  hasModeratorPermission?: boolean;
  onReplyClick?: (username: string) => void;
  isLoggedIn: boolean;
}

const MessageCard: React.FC<MessageCardProps> = ({
  originalId,
  isPresenter,
  isModerator,
  isQuestion,
  areActionsEnabled,
  hasModeratorPermission,
  message,
  user,
  createdDate,
  setMessageAsQuestion,
  setPinnedMessage,
  hideMessage,
  blockUser,
  onReplyClick,
  likeLiveQuestion,
  likedMessages,
  isLoggedIn,
  likes,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const menuRef = React.useRef(null);
  const [isOpenMenu, setIsOpenMenu] = useOpenState({ defaultOpen: false });
  const { languageKey } = useTranslation();

  if (!user) {
    return null;
  }

  const messageJsx = message && (
    <p
      className="font-body4 text-white "
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: unescapeChars(message),
      }}
    />
  );

  const usernameJsx = (
    <React.Fragment>
      <a
        className="tw-font-semibold break-all font-body4 text-white"
        href={user.profileUrl!}
        // @ts-ignore
        target="_blank"
        rel="noopener noreferrer"
        style={{
          minWidth: 80,
        }}
      >
        @{user.username}
      </a>
      {createdDate && (
        <p className="inline-block ml-8 text-base-lighter font-label">
          {date.toString(
            new Date(createdDate),
            'HH:mm:ss',
            languageKey as 'es' | 'pt',
          )}
        </p>
      )}
    </React.Fragment>
  );

  let iconColor = colors.baseLighter;

  if (isModerator) {
    iconColor = colors.primaryLighter;
  }
  if (isPresenter) {
    iconColor = colors.secondaryLight;
  }

  const likeButtonJsx = likeLiveQuestion && areActionsEnabled && (
    <div className="flex items-center">
      <p className="mr-4 font-label text-lighter">{likes}</p>
      <button
        type="button"
        onClick={() => likeLiveQuestion(originalId!)}
        className="outline-none"
      >
        {originalId && likedMessages?.includes(originalId) ? (
          <ThumbsUp5Fill size={24} color={colors.secondaryLight} />
        ) : (
          <ThumbsUp size={24} color={iconColor} />
        )}
      </button>
    </div>
  );

  const actionButtonJsx = hasModeratorPermission ? (
    <div className="flex items-center">
      {likeButtonJsx}
      <button
        ref={menuRef}
        type="button"
        onClick={() => setIsOpenMenu(prev => !prev)}
        className="outline-none ml-16"
        style={{ lineHeight: 0 }}
      >
        <Dots size={16} color={iconColor} />
      </button>
      <Popper
        anchorRef={menuRef}
        isVisible={isOpenMenu}
        dismissOnClickOutside
        onDismiss={() => setIsOpenMenu(false)}
        className="flex flex-col"
        position="left"
      >
        {onReplyClick ? (
          <MenuItem
            label={t('RESPONSE_COMMENT_LABEL')}
            onClick={() => {
              setIsOpenMenu(false);
              onReplyClick(user.username);
            }}
          />
        ) : null}
        {setMessageAsQuestion ? (
          <MenuItem
            label={
              isQuestion ? t('HIDDEN_QUESTION_LABEL') : t('MOVE_QUESTION_LABEL')
            }
            onClick={() => {
              setIsOpenMenu(false);
              setMessageAsQuestion(originalId!, !isQuestion);
            }}
          />
        ) : null}
        {setPinnedMessage ? (
          <MenuItem
            label={t('PIN_UP_COMMENT_LABEL')}
            onClick={() => {
              setIsOpenMenu(false);
              setPinnedMessage(originalId!);
            }}
          />
        ) : null}
        {hideMessage ? (
          <MenuItem
            label={t('HIDDEN_COMMENT_LABEL')}
            onClick={() => {
              setIsOpenMenu(false);
              hideMessage(originalId!);
            }}
          />
        ) : null}
        {blockUser ? (
          <MenuItem
            label={t('LOCK_USER_LABEL')}
            onClick={() => {
              setIsOpenMenu(false);
              blockUser(user.originalId!.toString());
            }}
          />
        ) : null}
      </Popper>
    </div>
  ) : isLoggedIn && onReplyClick ? (
    <button
      type="button"
      onClick={() => onReplyClick(user.username)}
      className="outline-none"
      style={{ transform: 'rotate(180deg)', lineHeight: 0 }}
    >
      <Reply size={16} color={iconColor} />
    </button>
  ) : isLoggedIn ? (
    likeButtonJsx
  ) : null;

  if (isPresenter) {
    return (
      <div className="p-8 mb-12 relative" style={{ borderRadius: 5 }}>
        <div
          className="absolute inset-0 bg-secondary-light pointer-events-none"
          style={{ opacity: '0.24', borderRadius: 5 }}
        />
        <div className="flex items-center justify-between">
          <p className="font-label text-secondary-light">{t('SPEAKER')}</p>
          {areActionsEnabled ? actionButtonJsx : null}
        </div>

        <div className="flex items-center mt-8">
          <Avatar
            alt={user.username}
            src={user.thumbnailUrl}
            size={16}
            href={user.profileUrl || undefined}
            // @ts-ignore
            target="_blank"
            rel="noopener noreferer"
          />
          <div className="ml-8">{usernameJsx}</div>
        </div>
        <div>{messageJsx}</div>
      </div>
    );
  }

  if (isModerator) {
    return (
      <div className="p-8 mb-12 relative">
        <div
          className="absolute inset-0 bg-primary-lighter pointer-events-none"
          style={{ opacity: '0.24', borderRadius: 5 }}
        />
        <div className="flex items-center justify-between">
          <p className="font-label text-primary-lighter">
            {t('MODERATOR_CHAT')}
          </p>

          {areActionsEnabled ? actionButtonJsx : null}
        </div>

        <div className="flex items-center mt-8">
          <Avatar
            alt={user.username}
            src={user.thumbnailUrl}
            size={16}
            href={user.profileUrl || undefined}
            // @ts-ignore
            target="_blank"
            rel="noopener noreferer"
          />
          <div className="ml-8">{usernameJsx}</div>
        </div>
        <div>{messageJsx}</div>
      </div>
    );
  }

  return (
    <div className="p-8 mb-12 bg-base-lighter-16" style={{ borderRadius: 5 }}>
      <div className="flex items-center justify-between">
        <div>{usernameJsx}</div>
        {areActionsEnabled ? actionButtonJsx : null}
      </div>

      {messageJsx}
    </div>
  );
};

export default React.memo(MessageCard);
